import React, { useState, useEffect } from 'react'
import { Button, Loader, Flex, Text, Heading, IconButton } from '@vibe/core'
import { Update } from '@vibe/icons'
import { Layout } from '../components/layout'
import { AuthGuard } from '../components/common/AuthGuard'
import { PermissionGuard } from '../components/common/PermissionGuard'
import { usePermissions } from '../hooks/usePermissions'
import { ApiService } from '../services/api'
import type { RoleInfo, GetRolesResponse } from '../types'
// Simple error handler
const handleApiError = (error: any): string => {
  return error?.message || 'Ha ocurrido un error'
}

interface RoleManagementProps {
  apiService?: ApiService
}

export const RoleManagement: React.FC<RoleManagementProps> = ({
  apiService = new ApiService(),
}) => {
  const permissions = usePermissions()

  // State management
  const [roles, setRoles] = useState<RoleInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Check permissions
  const canManageRoles = permissions?.canManageRoles() ?? false

  // Load roles on component mount
  useEffect(() => {
    loadRoles()
  }, [])

  /**
   * Load roles from API
   */
  const loadRoles = async () => {
    try {
      setLoading(true)
      setError(null)

      const response: GetRolesResponse = await apiService.getRoles()

      // Filtrar solo roles del sistema
      const systemRoles = response.data.items.filter(
        role =>
          role.is_system_role ||
          ['reclutador', 'solicitante', 'compensaciones'].includes(
            role.name.toLowerCase()
          )
      )

      setRoles(systemRoles)
    } catch (err) {
      const errorMessage = handleApiError(err)
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Get user count display
   */
  const getUserCountDisplay = (userCount: number) => {
    if (userCount === 0) {
      return <Text style={{ opacity: 0.7 }}>Sin usuarios</Text>
    }
    return (
      <Text>
        {userCount} {userCount === 1 ? 'usuario' : 'usuarios'}
      </Text>
    )
  }

  return (
    <AuthGuard>
      <PermissionGuard requirePermission={() => canManageRoles}>
        <Layout>
          <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <Flex justify="space-between" align="center">
                <div>
                  <Heading type="h1" className="mb-2">
                    Roles del Sistema
                  </Heading>
                  <Text type="text3" color="secondary" className="mt-1">
                    Roles predefinidos del sistema
                  </Text>
                </div>
              </Flex>
            </div>

            {/* Roles List */}
            <div className="bg-white rounded-lg shadow-sm">
              {!loading && roles.length > 0 && (
                <div className="space-y-4 p-6">
                  {roles.map(role => (
                    <div
                      key={role.id}
                      className="bg-gray-50 rounded-lg border border-gray-200 p-6"
                      data-testid={`role-row-${role.id}`}
                    >
                      <div className="flex-1">
                        <Text weight="medium" style={{ marginBottom: '8px' }}>
                          {role.name}
                        </Text>
                        <Text style={{ marginBottom: '8px' }}>
                          {role.description}
                        </Text>
                        <div>
                          <Text style={{ fontSize: '14px', opacity: 0.7 }}>
                            Usuarios asignados:
                          </Text>
                          {getUserCountDisplay(role.user_count)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {loading && roles.length === 0 && (
                <div className="p-8 text-center">
                  <Loader size="large" />
                  <Text className="mt-4">Cargando roles...</Text>
                </div>
              )}

              {error && !loading && roles.length === 0 && (
                <div className="p-8 text-center">
                  <Text color="negative" className="mb-4">
                    {error}
                  </Text>
                  <Button onClick={loadRoles}>Reintentar</Button>
                </div>
              )}

              {!loading && !error && roles.length === 0 && (
                <div className="p-8 text-center">
                  <Text className="mb-4">No hay roles disponibles.</Text>
                </div>
              )}
            </div>
          </div>
        </Layout>
      </PermissionGuard>
    </AuthGuard>
  )
}

export default RoleManagement
